import React, { useState, useContext, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers';
import { TextareaAutosize, FormControl, InputLabel, Select, MenuItem, Button, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import * as Constants from '../Constants';
import { AuthContext } from '../AuthContextProvider';
import ErrorDialog from '../ErrorDialog';
import { useNavigate, useParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { rpc } from '../utils/RpcUtils';
import { isValidDateTime } from '../utils/DateUtils';
import '../App.css';

const MOTD = () => {
  const [message, setMessage] = useState('');
  const [repeat, setRepeat] = useState('0');
  const [errorMessage, setErrorMessage] = useState('');
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const { user } = useContext(AuthContext);
  const timezone = user._site.timezone;
  const [selectedDate, setSelectedDate] = useState(DateTime.now().setZone(timezone));
  const navigate = useNavigate();
  let { id } = useParams();


  useEffect(() => {
    const onFetchSuccess = (motd) => {
      setSelectedDate(DateTime.fromISO(motd.date, { zone: timezone }));
      setMessage(motd.motd);
    };

    const fetchMotd = async (id) => {
      rpc({
        method: 'GetMotd',
        params: { _id: id },
        setErrorMessage,
        setOpenErrorDialog,
        onSuccess: onFetchSuccess
      });
    };
    if (id) {
      fetchMotd(id);
    }
  }, [id, timezone]);

  const handleDateChange = (date) => setSelectedDate(date);
  const handleMessageChange = (event) => setMessage(event.target.value);
  const handleRepeatChange = (event) => setRepeat(event.target.value);
  const onSuccess = () => navigate('/motds');

  const createMotd = async () => {
    if (!isValidDateTime(selectedDate) || message.trim() === '') {
      setErrorMessage('Date and Message fields are required');
      setOpenErrorDialog(true);
      return;
    }
    const dayOnly = selectedDate.setZone(timezone).startOf('day');
    const params = {
      _site: user._site._id,
      date: dayOnly.toISO(),
      motd: message,
      repeat: repeat
    };
    rpc({
      method: 'CreateMotd',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const updateMotd = async () => {
    if (!isValidDateTime(selectedDate) || message.trim() === '') {
      setErrorMessage('Date and Message fields are required');
      setOpenErrorDialog(true);
      return;
    }
    const params = {
      _id: id,
      date: selectedDate.toISO(),
      motd: message,
    };
    rpc({
      method: 'UpdateMotd',
      params,
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const deleteMotd = async () => {
    rpc({
      method: 'DeleteMotd',
      params: { _id: id },
      setErrorMessage,
      setOpenErrorDialog,
      onSuccess
    });
  };

  const handleClose = () => setOpenErrorDialog(false);

  const getMOTDFields = () => {
    if (id) {
      return (
        <div className="HorizontalContainer">
          <Button className="Button" variant="contained" color="primary" onClick={updateMotd}>
            Update
          </Button>
          <Button className="Button" variant="outlined" color="error" onClick={deleteMotd}>
            Delete
          </Button>
        </div>
      );
    } 
    return (
      <>
        <FormControl className="Select" sx={{ marginTop: "1em", width: 600 }}>
          <InputLabel id="repeat-label">Repeat</InputLabel>
          <Select
            labelId="repeat-label"
            id="repeat"
            value={repeat}
            label="Repeat"
            onChange={handleRepeatChange}
          >
            <MenuItem value="0">Do not repeat</MenuItem>
            <MenuItem value="1">1 day</MenuItem>
            <MenuItem value="2">2 days</MenuItem>
            <MenuItem value="3">3 days</MenuItem>
            <MenuItem value="4">4 days</MenuItem>
            <MenuItem value="5">5 days</MenuItem>
            <MenuItem value="6">6 days (one week)</MenuItem>
          </Select>
        </FormControl>
        <Button className="Button" variant="contained" color="primary" onClick={createMotd}>
          Create
        </Button>
      </>
    );
  };

  return (
    <div className="MOTDDetails">
      <Typography variant="h6" gutterBottom>
        Message of the Day (MOTD)
      </Typography>
      
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="en">
        <DatePicker
          className="DatePicker"
          label="Select Date"
          value={selectedDate}
          onChange={handleDateChange}
          slotProps={{ textField: { variant: 'outlined' } }}
          format={Constants.DATE_FORMAT_LUXON}
          disabled={!!id}
          minDate={DateTime.now()}
        />
      </LocalizationProvider>

      <TextareaAutosize
        className="TextareaAutosize"
        aria-label="Message"
        placeholder="Enter your message here"
        minRows={3}
        resize="none"
        value={message}
        onChange={handleMessageChange}
      />
      {getMOTDFields()}
      <ErrorDialog open={openErrorDialog} onErrorDialogClose={handleClose} errorMessage={errorMessage} />
    </div>
  );
};

export default MOTD;
